import { useFormContext } from '@context/FormContext';
import { Screens } from '@shared/constants/screens';
import { useAppContext } from '@context/appContext';
import { buildActiveJourney, resolveJourneyScreen } from 'utils/journeyOrchestrator';
import { Transition } from '@config/default.config';
import { useProgressHistory } from './useProgressHistory';

export type Phase = 'onLoad' | 'preSubmitOutcome' | 'postSubmitOutcome';

export const useScreenNavigator = () => {
  const { handleStep } = useFormContext();
  const { flowTransitions, screenOrder } = useAppContext();
  const [updateProgress] = useProgressHistory();

  // Ordered list of entry screens
  const activeJourney = buildActiveJourney(screenOrder);

  const resolveNextScreen = (currentScreen: string, phase: Phase, data: Record<string, unknown> = {}): Screens | null => {
    const screenTransitions = flowTransitions[currentScreen as Screens];

    if (!screenTransitions) {
      console.warn(`No transitions defined for: ${currentScreen}`);
      return null;
    }

    const phaseTransitions = screenTransitions[phase];

    if (!phaseTransitions?.length) {
      console.warn(`No "${phase}" transitions for: ${currentScreen}`);
      return null;
    }

    const matched = phaseTransitions.find((t: Transition) => !t.when || t.when(data));

    if (!matched) {
      console.warn(`No matching "${phase}" transition from: ${currentScreen}`);
      return null;
    }

    return matched.target;
  };

  const next = (currentScreen: string, phase: Phase, data: Record<string, unknown> = {}, shouldUpdateProgress = false) => {
    const resolvedScreen = resolveNextScreen(currentScreen, phase, data);
    if (!resolvedScreen) return;

    if (currentScreen === Screens.YOUR_INFORMATION_MANUALLY && activeJourney.includes(Screens.YOUR_INFO)) {
      currentScreen = Screens.YOUR_INFO;
    }
    const finalScreen = resolveJourneyScreen(resolvedScreen, currentScreen as Screens, activeJourney);

    if (finalScreen) {
      if (shouldUpdateProgress) {
        updateProgress({
          completedScreen: currentScreen as Screens,
          nextScreen: finalScreen,
        });
      }
      handleStep(finalScreen);
    }
  };

  return { next };
};
